import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import type { ReactNode, ComponentType } from 'react'
import * as React from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { PixelCatScreen } from '@/components/ui/PixelCatLoading'

// ── Lazy load helper：把 named export 包成 React.lazy 接受的 default-export 型 ──
function lazyNamed<T extends Record<string, any>, K extends keyof T>(
  loader: () => Promise<T>,
  name: K,
): React.LazyExoticComponent<ComponentType<any>> {
  return React.lazy(() => loader().then((m) => ({ default: m[name] as ComponentType<any> })))
}

// ── 所有 page 都 lazy load（每個 page 自成 chunk，main bundle 只剩 vendor + layout） ──
const LoginPageA = lazyNamed(() => import('@/pages/auth/LoginPage'), 'LoginPageA')
const RegisterPage = lazyNamed(() => import('@/pages/auth/RegisterPage'), 'RegisterPage')
const VerifyEmailPage = lazyNamed(() => import('@/pages/auth/VerifyEmailPage'), 'VerifyEmailPage')
const ResetPasswordPage = lazyNamed(() => import('@/pages/auth/ResetPasswordPage'), 'ResetPasswordPage')
const TermsPage = lazyNamed(() => import('@/pages/legal/LegalPage'), 'TermsPage')
const PrivacyPage = lazyNamed(() => import('@/pages/legal/LegalPage'), 'PrivacyPage')
const DataDeletionPage = lazyNamed(() => import('@/pages/legal/LegalPage'), 'DataDeletionPage')
const DashboardPageA = lazyNamed(() => import('@/pages/dashboard/DashboardPage'), 'DashboardPageA')
const CreateTripPageA = lazyNamed(() => import('@/pages/trips/CreateTripPage'), 'CreateTripPageA')
const TripDetailPage = lazyNamed(() => import('@/pages/trips/TripDetailPage'), 'TripDetailPage')
const TripEditPage = lazyNamed(() => import('@/pages/trips/TripEditPage'), 'TripEditPage')
const ExplorePage = lazyNamed(() => import('@/pages/explore/ExplorePage'), 'ExplorePage')
const AiChatPage = lazyNamed(() => import('@/pages/ai/AiChatPage'), 'AiChatPage')
const PointsPage = lazyNamed(() => import('@/pages/points/PointsPage'), 'PointsPage')
const AchievementsPage = lazyNamed(() => import('@/pages/achievements/AchievementsPage'), 'AchievementsPage')
const SettingsPage = lazyNamed(() => import('@/pages/settings/SettingsPage'), 'SettingsPage')
const ProfilePage = lazyNamed(() => import('@/pages/profile/ProfilePage'), 'ProfilePage')
const UserProfilePage = lazyNamed(() => import('@/pages/users/UserProfilePage'), 'UserProfilePage')
const NotificationsPage = lazyNamed(() => import('@/pages/notifications/NotificationsPage'), 'NotificationsPage')
const SubscriptionPage = lazyNamed(() => import('@/pages/subscription/SubscriptionPage'), 'SubscriptionPage')
const TasksPage = React.lazy(() => import('@/pages/tasks/TasksPage'))  // default export
const CouponPage = lazyNamed(() => import('@/pages/coupons/CouponPage'), 'CouponPage')
const RewardsPage = lazyNamed(() => import('@/pages/rewards/RewardsPage'), 'RewardsPage')
const AchievementsAdminPage = lazyNamed(() => import('@/pages/admin/AchievementsAdminPage'), 'AchievementsAdminPage')
const DmoPage = lazyNamed(() => import('@/pages/admin/DmoPage'), 'DmoPage')
const StorePage = lazyNamed(() => import('@/pages/stores/StorePage'), 'StorePage')
const MyStoresPage = lazyNamed(() => import('@/pages/stores/MyStoresPage'), 'MyStoresPage')
const MySalesPage = lazyNamed(() => import('@/pages/sales/MySalesPage'), 'MySalesPage')
const MyPurchasesPage = lazyNamed(() => import('@/pages/purchases/MyPurchasesPage'), 'MyPurchasesPage')
const AdMicrositePage = lazyNamed(() => import('@/pages/ads/AdMicrositePage'), 'AdMicrositePage')
const AdsAdminPage = lazyNamed(() => import('@/pages/admin/AdsAdminPage'), 'AdsAdminPage')
const PaymentPage = lazyNamed(() => import('@/pages/payment/PaymentPage'), 'PaymentPage')
const PaymentSuccessPage = lazyNamed(() => import('@/pages/payment/PaymentSuccessPage'), 'PaymentSuccessPage')
const AdminDashboardPage = lazyNamed(() => import('@/pages/admin/AdminDashboardPage'), 'AdminDashboardPage')
const TenantsAdminPage = lazyNamed(() => import('@/pages/admin/TenantsAdminPage'), 'TenantsAdminPage')
const LoyaltyAdminPage = lazyNamed(() => import('@/pages/admin/LoyaltyAdminPage'), 'LoyaltyAdminPage')
const ReportsAdminPage = lazyNamed(() => import('@/pages/admin/ReportsAdminPage'), 'ReportsAdminPage')
const SupportAdminPage = lazyNamed(() => import('@/pages/admin/SupportAdminPage'), 'SupportAdminPage')
const DisputeAdminPage = lazyNamed(() => import('@/pages/admin/DisputeAdminPage'), 'DisputeAdminPage')
const SecurityAdminPage = lazyNamed(() => import('@/pages/admin/SecurityAdminPage'), 'SecurityAdminPage')
const VlogPage = lazyNamed(() => import('@/pages/vlogs/VlogPage'), 'VlogPage')
const VlogEditorPage = lazyNamed(() => import('@/pages/vlogs/VlogEditorPage'), 'VlogEditorPage')
const WikiBrowsePage = lazyNamed(() => import('@/pages/wiki/WikiBrowsePage'), 'WikiBrowsePage')
const ExpensesPage = lazyNamed(() => import('@/pages/expenses/ExpensesPage'), 'ExpensesPage')

import { GlobalErrorBoundary } from '@/components/GlobalErrorBoundary'
import { useAuthStore } from '@/store/auth'
import { SupportChat } from '@/components/support/SupportChat'
import { AppLayout } from '@/components/layout/AppLayout'

// ── 走路像素機器人（2 幀循環）────────────────────────────────────────────────
function WalkingRobot() {
  const [frame, setFrame] = React.useState(0)
  React.useEffect(() => {
    const id = setInterval(() => setFrame((f) => (f + 1) % 2), 320)
    return () => clearInterval(id)
  }, [])

  // 共用顏色
  const C = 'var(--color-coral)'
  const N = 'var(--color-navy)'
  const S = 'var(--color-sky)'
  const Y = 'var(--color-sun)'
  const B = 'var(--color-border)'
  const W = '#fff9e6'

  // 機身 y 偏移（上下晃動）：frame 0 正常，frame 1 上移 1px
  const bobY = frame === 0 ? 0 : -1

  // 腿部：frame0 左前右後，frame1 右前左後
  const leftLegY  = frame === 0 ? 14 : 16  // 左腿 foot y
  const rightLegY = frame === 0 ? 16 : 14  // 右腿 foot y
  const leftFootX = frame === 0 ? 4  : 5   // 左腳 x（往前伸）
  const rightFootX= frame === 0 ? 9  : 8   // 右腳 x（往前伸）

  // 手臂：frame0 左前右後，frame1 右前左後
  const leftArmY  = frame === 0 ? 8 : 9
  const rightArmY = frame === 0 ? 9 : 8

  return (
    <svg viewBox="0 0 16 22" width={80} height={110} shapeRendering="crispEdges">
      <g transform={`translate(0,${bobY})`}>
        {/* 天線 */}
        <rect x="7" y="0" width="2" height="1" fill={Y} />
        <rect x="7" y="1" width="2" height="1" fill={B} />

        {/* 頭 */}
        <rect x="4" y="2" width="8" height="1" fill={B} />
        <rect x="3" y="3" width="1" height="5" fill={B} />
        <rect x="12" y="3" width="1" height="5" fill={B} />
        <rect x="4" y="3" width="8" height="5" fill={C} />
        <rect x="4" y="7" width="8" height="1" fill={B} />
        {/* 眼睛 */}
        <rect x="5" y="4" width="2" height="2" fill={N} />
        <rect x="9" y="4" width="2" height="2" fill={N} />
        <rect x="5" y="4" width="1" height="1" fill={W} />
        <rect x="9" y="4" width="1" height="1" fill={W} />

        {/* 左手臂 */}
        <rect x="1" y={leftArmY}  width="3" height="2" fill={C} />
        <rect x="1" y={leftArmY}  width="3" height="1" fill={B} />
        {/* 右手臂 */}
        <rect x="12" y={rightArmY} width="3" height="2" fill={C} />
        <rect x="12" y={rightArmY} width="3" height="1" fill={B} />

        {/* 身體 */}
        <rect x="4" y="8"  width="8" height="1" fill={B} />
        <rect x="4" y="9"  width="8" height="5" fill={N} />
        <rect x="3" y="9"  width="1" height="5" fill={B} />
        <rect x="12" y="9" width="1" height="5" fill={B} />
        <rect x="4" y="13" width="8" height="1" fill={B} />
        {/* 胸燈 */}
        <rect x="7" y="10" width="2" height="2" fill={S} />

        {/* 左腿 */}
        <rect x="5" y="14" width="2" height="3" fill={C} />
        <rect x="5" y="14" width="1" height="3" fill={B} />
        <rect x={leftFootX} y={leftLegY} width="3" height="1" fill={N} />

        {/* 右腿 */}
        <rect x="9" y="14" width="2" height="3" fill={C} />
        <rect x="10" y="14" width="1" height="3" fill={B} />
        <rect x={rightFootX} y={rightLegY} width="3" height="1" fill={N} />

        {/* 地影 */}
        <rect x="4" y="21" width="8" height="1" fill={B} opacity="0.2" />
      </g>
    </svg>
  )
}

// ── 全頁佔位畫面 ──────────────────────────────────────────────────────────────
function BusyScreen({ onRetry }: { onRetry?: () => void }) {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 24,
        background: 'var(--color-background)',
      }}
    >
      <WalkingRobot />

      <div style={{ textAlign: 'center' }}>
        <p
          className="font-pixel animate-pulse"
          style={{ fontSize: 11, color: 'var(--color-foreground)', letterSpacing: '0.12em', marginBottom: 8 }}
        >
          系統忙碌中，請稍後再試
        </p>
        <p style={{ fontSize: 12, color: 'var(--color-muted)' }}>
          SYSTEM BUSY · PLEASE WAIT
        </p>
      </div>

      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            padding: '8px 20px',
            border: '2px solid var(--color-border)',
            background: 'var(--color-surface)',
            boxShadow: '3px 3px 0 0 var(--color-border)',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 13,
            color: 'var(--color-foreground)',
          }}
        >
          重新整理
        </button>
      )}
    </div>
  )
}

// ── ErrorBoundary：捕捉渲染錯誤，避免空白頁面 ────────────────────────────────
class ErrorBoundary extends React.Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state = { error: null }
  static getDerivedStateFromError(error: Error) { return { error } }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info)
  }
  render() {
    if (this.state.error) {
      return <BusyScreen onRetry={() => { this.setState({ error: null }); window.location.reload() }} />
    }
    return this.props.children
  }
}

// ── LocationKey：換頁時重置對應 ErrorBoundary ─────────────────────────────────
function LocationErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation()
  return <ErrorBoundary key={location.key}>{children}</ErrorBoundary>
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60_000, retry: 1 },
  },
})

function RequireAuth({ children }: { children: ReactNode }) {
  // H1 後 token 走 HttpOnly cookie，前端 store 改靠 user 判斷登入狀態
  const user = useAuthStore((s) => s.user)
  const hasHydrated = useAuthStore((s) => s._hasHydrated)
  const setHasHydrated = useAuthStore((s) => s.setHasHydrated)
  const setAuth = useAuthStore((s) => s.setAuth)
  const clearAuth = useAuthStore((s) => s.clearAuth)
  const [countdown, setCountdown] = React.useState(3)

  React.useEffect(() => {
    if (!hasHydrated) {
      const t = setTimeout(() => setHasHydrated(true), 300)
      return () => clearTimeout(t)
    }
  }, [hasHydrated, setHasHydrated])

  // hydration 完成後背景同步 cookie 對應的真實 user 到 store，
  // 避免 zustand persist 殘留前一個 session（例如切換帳號時 store stale →
  // isOwner 判斷錯 → owner 看不到 edit 入口）
  React.useEffect(() => {
    if (!hasHydrated) return
    let cancelled = false
    ;(async () => {
      try {
        const r = await fetch('/api/auth/me', { credentials: 'include' })
        if (cancelled) return
        if (r.status === 401 || r.status === 403) {
          if (user) await clearAuth()
          return
        }
        if (!r.ok) return
        const data = await r.json() as { id: string; display_name: string; roles: string[] }
        if (cancelled) return
        // 真實 user 跟 store 不一致 → 強制覆寫
        if (!user || user.id !== data.id) {
          setAuth(data as Parameters<typeof setAuth>[0])
        }
      } catch { /* network ignore */ }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHydrated])

  // L4：未登入 → 帶當前路徑作為 redirect 參數
  const loginUrl = React.useMemo(() => {
    const cur = window.location.pathname + window.location.search
    return cur === '/' || cur.startsWith('/login')
      ? '/login'
      : `/login?redirect=${encodeURIComponent(cur)}`
  }, [])

  React.useEffect(() => {
    if (!hasHydrated || user) return
    if (countdown <= 0) {
      window.location.replace(loginUrl)
      return
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000)
    return () => clearTimeout(t)
  }, [hasHydrated, user, countdown, loginUrl])

  if (!hasHydrated) return <PixelCatScreen />

  if (!user) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 20,
          background: 'var(--color-background)',
        }}
      >
        <svg viewBox="0 0 32 32" width={56} height={56} shapeRendering="crispEdges">
          <rect x="12" y="4"  width="8"  height="2" fill="var(--color-coral)" />
          <rect x="10" y="6"  width="12" height="2" fill="var(--color-coral)" />
          <rect x="8"  y="8"  width="16" height="8" fill="var(--color-coral)" />
          <rect x="10" y="10" width="4"  height="4" fill="var(--color-background)" />
          <rect x="18" y="10" width="4"  height="4" fill="var(--color-background)" />
          <rect x="8"  y="18" width="16" height="2" fill="var(--color-coral)" />
          <rect x="6"  y="20" width="20" height="6" fill="var(--color-navy)" />
          <rect x="10" y="24" width="4"  height="4" fill="var(--color-navy)" />
          <rect x="18" y="24" width="4"  height="4" fill="var(--color-navy)" />
        </svg>
        <div style={{ textAlign: 'center' }}>
          <p
            className="font-pixel"
            style={{ fontSize: 12, color: 'var(--color-coral)', letterSpacing: '0.1em', marginBottom: 8 }}
          >
            尚未登入
          </p>
          <p style={{ fontSize: 13, color: 'var(--color-muted)', marginBottom: 4 }}>
            {countdown} 秒後自動跳轉至登入頁面…
          </p>
        </div>
        <a
          href={loginUrl}
          style={{
            padding: '8px 24px',
            border: '2px solid var(--color-border)',
            background: 'var(--color-sky)',
            boxShadow: '3px 3px 0 0 var(--color-border)',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 13,
            color: 'var(--color-foreground)',
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          立即前往登入
        </a>
      </div>
    )
  }

  return <>{children}</>
}

function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <RequireAuth>
      <AppLayout>{children}</AppLayout>
    </RequireAuth>
  )
}

export default function App() {
  return (
    <GlobalErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <LocationErrorBoundary>
        <React.Suspense fallback={<PixelCatScreen />}>
        <Routes>
          <Route path="/login" element={<LoginPageA />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/data-deletion" element={<DataDeletionPage />} />
          <Route path="/dashboard"      element={<AuthLayout><DashboardPageA /></AuthLayout>} />
          <Route path="/trips/new"      element={<AuthLayout><CreateTripPageA /></AuthLayout>} />
          <Route path="/trips/:id"      element={<AuthLayout><TripDetailPage /></AuthLayout>} />
          <Route path="/trips/:id/edit" element={<AuthLayout><TripEditPage /></AuthLayout>} />
          <Route path="/explore"        element={<AuthLayout><ExplorePage /></AuthLayout>} />
          <Route path="/ai/chat"        element={<AuthLayout><AiChatPage /></AuthLayout>} />
          <Route path="/achievements"    element={<AuthLayout><AchievementsPage /></AuthLayout>} />
          <Route path="/points"          element={<AuthLayout><PointsPage /></AuthLayout>} />
          <Route path="/settings"       element={<AuthLayout><SettingsPage /></AuthLayout>} />
          <Route path="/profile"        element={<AuthLayout><ProfilePage /></AuthLayout>} />
          <Route path="/users/:id"      element={<AuthLayout><UserProfilePage /></AuthLayout>} />
          <Route path="/notifications"  element={<AuthLayout><NotificationsPage /></AuthLayout>} />
          <Route path="/subscription"   element={<AuthLayout><SubscriptionPage /></AuthLayout>} />
          <Route path="/tasks"          element={<AuthLayout><TasksPage /></AuthLayout>} />
          <Route path="/coupons"        element={<AuthLayout><CouponPage /></AuthLayout>} />
          <Route path="/rewards"            element={<AuthLayout><RewardsPage /></AuthLayout>} />
          <Route path="/admin/achievements" element={<AuthLayout><AchievementsAdminPage /></AuthLayout>} />
          <Route path="/admin/dmo"          element={<AuthLayout><DmoPage /></AuthLayout>} />
          <Route path="/stores/:id"     element={<StorePage />} />
          <Route path="/my-stores"      element={<AuthLayout><MyStoresPage /></AuthLayout>} />
          <Route path="/my-sales"       element={<AuthLayout><MySalesPage /></AuthLayout>} />
          <Route path="/my-purchases"   element={<AuthLayout><MyPurchasesPage /></AuthLayout>} />
          {/* 廣告 microsite 中介頁（公開；不需登入即可瀏覽） */}
          <Route path="/ads/:slug"      element={<AdMicrositePage />} />
          <Route path="/admin/ads"       element={<AuthLayout><AdsAdminPage /></AuthLayout>} />
          <Route path="/admin/dashboard" element={<AuthLayout><AdminDashboardPage /></AuthLayout>} />
          <Route path="/admin/tenants"   element={<AuthLayout><TenantsAdminPage /></AuthLayout>} />
          <Route path="/admin/loyalty"   element={<AuthLayout><LoyaltyAdminPage /></AuthLayout>} />
          <Route path="/admin/reports"   element={<AuthLayout><ReportsAdminPage /></AuthLayout>} />
          <Route path="/admin/support"   element={<AuthLayout><SupportAdminPage /></AuthLayout>} />
          <Route path="/admin/disputes"  element={<AuthLayout><DisputeAdminPage /></AuthLayout>} />
          <Route path="/admin/security"  element={<AuthLayout><SecurityAdminPage /></AuthLayout>} />
          <Route path="/vlogs"             element={<AuthLayout><VlogPage /></AuthLayout>} />
          <Route path="/vlogs/new"         element={<AuthLayout><VlogEditorPage /></AuthLayout>} />
          <Route path="/vlogs/:id/edit"    element={<AuthLayout><VlogEditorPage /></AuthLayout>} />
          <Route path="/wiki"              element={<WikiBrowsePage />} />
          <Route path="/expenses"          element={<AuthLayout><ExpensesPage /></AuthLayout>} />
          <Route path="/payment/:id/mock-confirm" element={<AuthLayout><PaymentPage /></AuthLayout>} />
          <Route path="/payment/success" element={<AuthLayout><PaymentSuccessPage /></AuthLayout>} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </React.Suspense>
        </LocationErrorBoundary>
        <SupportChat />
      </BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: 'var(--color-surface)',
            color: 'var(--color-foreground)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
          },
        }}
      />
    </QueryClientProvider>
    </GlobalErrorBoundary>
  )
}
